import { Link, router } from '@inertiajs/react'
import { Activity, Archive, ArrowLeft, MessageSquareText, MoreHorizontal, RefreshCw, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { openBusinessProcessEditChat } from '@/frontend/features/AgentChats/assistant-ui/businessProcessEditChatBus'
import ProcessProgressBadge from './ProcessProgressBadge'
import StatusBadge from './StatusBadge'
import type { BusinessProcessDetail, EditChatWorker } from './types'

// Page header: breadcrumb, title + status, worker/version subline, and the
// management actions. The primary lifecycle action lives in ProcessActionPanel;
// destructive/archive actions remain behind the overflow menu. Editing and
// operation navigation are page-level actions, so they live in the header
// instead of covering content in a fixed bottom dock.
export default function ProcessHeader({
  pageTitle,
  process,
  operationsPath,
  editChat,
  onDeleteStart,
  onDeleteFailed,
}: {
  pageTitle: string
  process: BusinessProcessDetail
  operationsPath?: string
  editChat?: EditChatWorker
  onDeleteStart?: () => void
  onDeleteFailed?: () => void
}) {
  const canDelete = process.can_delete && !process.can_cancel
  const canArchive = !process.archived
  const canRetryPendingPublication =
    process.progress_kind === 'publication_pending' && process.can_retry_draft && Boolean(process.current_version?.id)
  const showOverflow = process.can_manage && (canArchive || canDelete || process.can_cancel || canRetryPendingPublication)
  const editChatReady = Boolean(editChat?.chat_ready && editChat?.intake_enabled)
  const editDisabledReason = !editChat || editChat.chat_ready
    ? (editChat && !editChat.intake_enabled
      ? `A edição de processos por conversa está desativada para ${editChat.name}.`
      : null)
    : `${editChat.name} não está disponível para conversar agora.`
  const archive = () => router.post(`/dashboard/business_processes/${process.id}/archive`, {}, { preserveScroll: true })
  const remove = () => {
    if (!window.confirm('Excluir este processo? As instruções disponibilizadas também serão removidas do agente.')) return
    onDeleteStart?.()
    router.delete(`/dashboard/business_processes/${process.id}`, {
      preserveScroll: true,
      onCancel: () => onDeleteFailed?.(),
      onError: () => onDeleteFailed?.(),
      // Falhas de remoção redirecionam de volta para esta página com flash.
      // No sucesso, a rota já mudou para a listagem e o componente desmonta.
      onFinish: () => {
        if (window.location.pathname === `/dashboard/business_processes/${process.id}`) onDeleteFailed?.()
      },
    })
  }
  const cancelAgent = () => router.post(`/dashboard/business_processes/${process.id}/cancel_agent`, {}, { preserveScroll: true })
  const retryPendingPublication = () => {
    const versionId = process.current_version?.id
    if (!versionId) return
    router.post(`/dashboard/business_processes/${process.id}/approve_draft`, { version_id: versionId }, { preserveScroll: true })
  }
  const startEditChat = () => {
    if (!editChat || !editChatReady) return

    openBusinessProcessEditChat({ processId: process.id, workerId: editChat.id })
  }
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
          <Link href="/dashboard/business_processes">
            <ArrowLeft className="size-4" />
            Processos
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{pageTitle}</h1>
          <StatusBadge status={process.status} label={process.status_label} />
          <ProcessProgressBadge progressKind={process.progress_kind} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {process.can_view_technical_details && `Identificador: ${process.skill_slug} · `}{process.digital_worker_name}{process.can_view_technical_details && ` · Versão ${process.current_version_number || '-'}`}
        </p>
      </div>

      {(operationsPath || process.can_edit || process.can_manage) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="process-header-actions">
          {operationsPath && (
            <Button variant="outline" size="sm" asChild>
              <Link href={operationsPath}>
                <Activity aria-hidden="true" className="size-4" />
                Operação
              </Link>
            </Button>
          )}
          {process.can_edit && (
            <Button
              size="sm"
              data-testid="editar-processo-chat"
              disabled={!editChatReady}
              title={editDisabledReason ?? undefined}
              onClick={startEditChat}
            >
              <MessageSquareText aria-hidden="true" className="size-4" />
              Editar com agente
            </Button>
          )}
          {process.can_manage && showOverflow && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Mais ações">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canRetryPendingPublication && (
                  <DropdownMenuItem onClick={retryPendingPublication}>
                    <RefreshCw className="size-4" />
                    Tentar novamente
                  </DropdownMenuItem>
                )}
                {process.can_cancel && (
                  <DropdownMenuItem onClick={cancelAgent}>
                    <RotateCcw className="size-4" />
                    Cancelar preparação
                  </DropdownMenuItem>
                )}
                {canArchive && (
                  <DropdownMenuItem onClick={archive}>
                    <Archive className="size-4" />
                    Arquivar
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem variant="destructive" onClick={remove}>
                    <Trash2 className="size-4" />
                    Excluir
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  )
}
